/**
 * In-app mock of the admin API (build plan P2: "build a thin mock layer …
 * toggled by VITE_ADMIN_API_MODE"). It responds to every OpenAPI operation
 * with fixture data shaped from `@attendik/types`, so type safety holds and
 * the UI is fully exercisable before admin-api (P1) is available.
 *
 * The mock keeps mutable in-memory state for the session — creating a
 * customer, assigning an operator, running a sync etc. all persist until the
 * page reloads. It is intentionally lenient: it focuses on shapes and the
 * scope/overlap/sync behaviours the UI must render, not on full backend
 * fidelity.
 */
import type {
  AdminUser,
  AssignedOperator,
  Attendee,
  AuditEntry,
  CreateStageRequest,
  Customer,
  DashboardSummary,
  Event,
  LiveCounts,
  MeetpointConfig,
  MeetpointEventSummary,
  Operator,
  Scan,
  Stage,
  StageOperator,
  SyncRun,
  UpdateStageRequest
} from "@attendik/types/admin-schemas";

import { ApiError } from "./errors.js";

// ---- Token helpers ----------------------------------------------------------
// The auth store decodes the JWT payload (base64url middle segment). The mock
// issues a structurally valid 3-segment token whose payload carries the claims.

interface TokenClaims {
  sub: string;
  email: string;
  name: string;
  role: "portal_admin" | "customer_admin";
  customer_id: string | null;
  exp: number;
}

function base64UrlEncode(input: string): string {
  // btoa handles ASCII; admin emails/names are ASCII in fixtures.
  return btoa(input).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function makeToken(claims: TokenClaims): string {
  const header = base64UrlEncode(JSON.stringify({ alg: "none", typ: "JWT" }));
  const payload = base64UrlEncode(JSON.stringify(claims));
  return `${header}.${payload}.mock-signature`;
}

function decodeToken(token: string): TokenClaims | null {
  try {
    const seg = token.split(".")[1];
    if (!seg) {
      return null;
    }
    const json = atob(seg.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as TokenClaims;
  } catch {
    return null;
  }
}

// ---- Deterministic ids ------------------------------------------------------

const CUSTOMER_ACME = "11111111-1111-1111-1111-111111111111";
const CUSTOMER_GLOBEX = "22222222-2222-2222-2222-222222222222";

const PORTAL_ADMIN_ID = "aaaaaaaa-0000-0000-0000-000000000001";
const ACME_ADMIN_ID = "aaaaaaaa-0000-0000-0000-000000000002";
const GLOBEX_ADMIN_ID = "aaaaaaaa-0000-0000-0000-000000000003";

const EVENT_ACME_1 = "ee000000-0000-0000-0000-000000000001";
const EVENT_ACME_2 = "ee000000-0000-0000-0000-000000000002";
const EVENT_GLOBEX_1 = "ee000000-0000-0000-0000-000000000003";

const OP_ACME_1 = "00000000-0000-0000-0000-0000000000a1";
const OP_ACME_2 = "00000000-0000-0000-0000-0000000000a2";
const OP_GLOBEX_1 = "00000000-0000-0000-0000-0000000000b1";

let idCounter = 1000;
function newId(): string {
  idCounter += 1;
  const hex = idCounter.toString(16).padStart(12, "0");
  return `ffffffff-0000-0000-0000-${hex}`;
}

// CR-07. Mirrors the server's normaliseGroupNames so the mock surfaces
// the same trim / drop-empties / dedup semantics. Used by attendee
// create and update.
function normaliseMockGroupNames(input: string[] | undefined): string[] {
  if (!input) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input) {
    const trimmed = raw.trim();
    if (trimmed.length === 0 || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

function nowIso(): string {
  return new Date().toISOString();
}

function isoOffset(hours: number): string {
  return new Date(Date.now() + hours * 3600 * 1000).toISOString();
}

// ---- Credentials fixture ----------------------------------------------------

interface MockCredential {
  email: string;
  password: string;
  userId: string;
}

const CREDENTIALS: MockCredential[] = [
  { email: "admin@attendik.local", password: "Admin123!", userId: PORTAL_ADMIN_ID },
  { email: "admin@acme.local", password: "Admin123!", userId: ACME_ADMIN_ID },
  { email: "admin@globex.local", password: "Admin123!", userId: GLOBEX_ADMIN_ID }
];

// ---- Mutable in-memory state ------------------------------------------------

interface MockState {
  customers: Customer[];
  adminUsers: AdminUser[];
  events: Event[];
  operators: Operator[];
  eventOperators: { event_id: string; operator_id: string }[];
  stages: Stage[];
  stageOperators: { stage_id: string; operator_id: string; created_at: string }[];
  attendees: Attendee[];
  scans: Scan[];
  meetpointConfigs: Map<string, MeetpointConfig>;
  syncRuns: SyncRun[];
  audit: AuditEntry[];
  // password reset for newly-created users (so login can succeed in a session)
  extraCredentials: MockCredential[];
  // Monitor CR-02 — per-event Snapshot recipient lists.
  snapshotRecipients: Map<string, { id: string; email: string; created_at: string; created_by: string | null }[]>;
}

function seedState(): MockState {
  const customers: Customer[] = [
    {
      id: CUSTOMER_ACME,
      name: "Acme Events",
      slug: "acme-events",
      status: "ACTIVE",
      created_at: isoOffset(-720)
    },
    {
      id: CUSTOMER_GLOBEX,
      name: "Globex Conferences",
      slug: "globex",
      status: "ACTIVE",
      created_at: isoOffset(-240)
    }
  ];

  const adminUsers: AdminUser[] = [
    {
      id: PORTAL_ADMIN_ID,
      email: "admin@attendik.local",
      name: "Portal Admin",
      role: "portal_admin",
      customer_id: null,
      status: "ACTIVE",
      last_login_at: isoOffset(-2),
      created_at: isoOffset(-1000)
    },
    {
      id: ACME_ADMIN_ID,
      email: "admin@acme.local",
      name: "Acme Administrator",
      role: "customer_admin",
      customer_id: CUSTOMER_ACME,
      status: "ACTIVE",
      last_login_at: isoOffset(-26),
      created_at: isoOffset(-700)
    },
    {
      id: GLOBEX_ADMIN_ID,
      email: "admin@globex.local",
      name: "Globex Administrator",
      role: "customer_admin",
      customer_id: CUSTOMER_GLOBEX,
      status: "ACTIVE",
      last_login_at: isoOffset(-50),
      created_at: isoOffset(-230)
    }
  ];

  const events: Event[] = [
    {
      id: EVENT_ACME_1,
      customer_id: CUSTOMER_ACME,
      name: "Acme Spring Summit 2026",
      active_from: isoOffset(-1),
      active_to: isoOffset(5),
      status: "ACTIVE",
      capacity: 300,
      webhook_url: "https://monitor.acme.example/hooks/spring",
      created_at: isoOffset(-100),
      went_live_at: null,
      meetpoint_polling_enabled: false
    },
    {
      id: EVENT_ACME_2,
      customer_id: CUSTOMER_ACME,
      name: "Acme Partner Day 2025",
      active_from: isoOffset(-2000),
      active_to: isoOffset(-1990),
      status: "CLOSED",
      capacity: 120,
      webhook_url: null,
      created_at: isoOffset(-2100),
      went_live_at: null,
      meetpoint_polling_enabled: false
    },
    {
      id: EVENT_GLOBEX_1,
      customer_id: CUSTOMER_GLOBEX,
      name: "Globex Annual 2026",
      active_from: isoOffset(48),
      active_to: isoOffset(56),
      status: "ACTIVE",
      capacity: 500,
      webhook_url: null,
      created_at: isoOffset(-48),
      went_live_at: null,
      meetpoint_polling_enabled: false
    }
  ];

  const operators: Operator[] = [
    {
      id: OP_ACME_1,
      customer_id: CUSTOMER_ACME,
      email: "door1@acme.local",
      name: "Acme Door One",
      status: "ACTIVE",
      created_at: isoOffset(-200)
    },
    {
      id: OP_ACME_2,
      customer_id: CUSTOMER_ACME,
      email: "door2@acme.local",
      name: "Acme Door Two",
      status: "ACTIVE",
      created_at: isoOffset(-150)
    },
    {
      id: OP_GLOBEX_1,
      customer_id: CUSTOMER_GLOBEX,
      email: "door1@globex.local",
      name: "Globex Door One",
      status: "DISABLED",
      created_at: isoOffset(-30)
    }
  ];

  const eventOperators = [{ event_id: EVENT_ACME_1, operator_id: OP_ACME_1 }];

  const attendees: Attendee[] = [];
  // CR-07. Sample group labels per index so the admin UI mock shows the
  // new column round-tripping. Real data comes from Meetpoint sync.
  const MOCK_GROUPS: string[][] = [
    ["SPEAKER", "VIP DINNER"],
    ["NETWORKING"],
    ["TELEMATICS", "EN prijavljeni B2B"],
    [],
    ["SPEAKER"],
    []
  ];
  for (let i = 1; i <= 6; i += 1) {
    attendees.push({
      id: `dddddddd-0000-0000-0000-${i.toString(16).padStart(12, "0")}`,
      event_id: EVENT_ACME_1,
      badge_id: `cccccccc-0000-0000-0000-${i.toString(16).padStart(12, "0")}`,
      name: `Acme Attendee ${i}`,
      email: `attendee${i}@acme.example`,
      company: i % 2 === 0 ? "Acme Corp" : "Partner Ltd",
      group_names: MOCK_GROUPS[i - 1] ?? [],
      source: i <= 4 ? "meetpoint" : "manual",
      status: i === 1 ? "IN_ROOM" : i === 2 ? "OUT_OF_ROOM" : "NOT_SCANNED",
      meetpoint_participant_id: i <= 4 ? 1000 + i : null,
      meetpoint_contact_id: i <= 4 ? 5000 + i : null,
      created_at: isoOffset(-90),
      updated_at: isoOffset(-90)
    });
  }

  const scans: Scan[] = [
    {
      id: newId(),
      event_id: EVENT_ACME_1,
      attendee_id: attendees[0]!.id,
      badge_id: attendees[0]!.badge_id,
      direction: "ENTER",
      result_code: null,
      scanned_by: OP_ACME_1,
      scanned_at: isoOffset(-0.5)
    },
    {
      id: newId(),
      event_id: EVENT_ACME_1,
      attendee_id: attendees[1]!.id,
      badge_id: attendees[1]!.badge_id,
      direction: "ENTER",
      result_code: null,
      scanned_by: OP_ACME_1,
      scanned_at: isoOffset(-0.4)
    },
    {
      id: newId(),
      event_id: EVENT_ACME_1,
      attendee_id: attendees[1]!.id,
      badge_id: attendees[1]!.badge_id,
      direction: "EXIT",
      result_code: null,
      scanned_by: OP_ACME_1,
      scanned_at: isoOffset(-0.3)
    }
  ];

  const meetpointConfigs = new Map<string, MeetpointConfig>();
  meetpointConfigs.set(EVENT_ACME_1, {
    event_id: EVENT_ACME_1,
    base_url: "https://platform.meetpoint.si",
    username: "acme-integration",
    password: "••••••••",
    meetpoint_event_id: "313",
    language_id: "EN",
    invitation_status_ids: [-1],
    polling_enabled: true,
    polling_interval_seconds: 30,
    // Seed a failing latest poll so the dashboard "Needs attention" panel is
    // exercisable in the mock playground (polling on + last sync FAILED →
    // POLLING_FAILING). The dashboard derives sync health from the sync log.
    last_synced_at: isoOffset(-0.1),
    last_sync_status: "FAILED",
    last_sync_error: "Meetpoint timed out after 10s",
    created_at: isoOffset(-90),
    updated_at: isoOffset(-0.1)
  });

  const syncRuns: SyncRun[] = [
    {
      id: newId(),
      event_id: EVENT_ACME_1,
      mode: "INITIAL",
      triggered_by: ACME_ADMIN_ID,
      started_at: isoOffset(-90),
      finished_at: isoOffset(-90),
      status: "SUCCESS",
      inserted_count: 4,
      updated_count: 0,
      skipped_count: 0,
      // CR-06: every sync row now carries stage counters too.
      stage_inserted_count: 0,
      stage_updated_count: 0,
      stage_skipped_count: 0,
      error: null
    },
    {
      id: newId(),
      event_id: EVENT_ACME_1,
      mode: "POLLED",
      triggered_by: null,
      started_at: isoOffset(-0.2),
      finished_at: isoOffset(-0.2),
      status: "SUCCESS",
      inserted_count: 0,
      updated_count: 0,
      skipped_count: 4,
      stage_inserted_count: 0,
      stage_updated_count: 0,
      stage_skipped_count: 0,
      error: null
    },
    {
      // Most recent run — a failed poll, so the dashboard surfaces it.
      id: newId(),
      event_id: EVENT_ACME_1,
      mode: "POLLED",
      triggered_by: null,
      started_at: isoOffset(-0.1),
      finished_at: isoOffset(-0.1),
      status: "FAILED",
      inserted_count: 0,
      updated_count: 0,
      skipped_count: 0,
      stage_inserted_count: 0,
      stage_updated_count: 0,
      stage_skipped_count: 0,
      error: "Meetpoint timed out after 10s"
    }
  ];

  const audit: AuditEntry[] = [
    {
      id: newId(),
      admin_user_id: ACME_ADMIN_ID,
      customer_id: CUSTOMER_ACME,
      action: "event.create",
      entity_type: "event",
      entity_id: EVENT_ACME_1,
      diff: null,
      created_at: isoOffset(-100)
    },
    {
      id: newId(),
      admin_user_id: PORTAL_ADMIN_ID,
      customer_id: CUSTOMER_GLOBEX,
      action: "customer.create",
      entity_type: "customer",
      entity_id: CUSTOMER_GLOBEX,
      diff: null,
      created_at: isoOffset(-240)
    },
    {
      id: newId(),
      admin_user_id: ACME_ADMIN_ID,
      customer_id: CUSTOMER_ACME,
      action: "event.update",
      entity_type: "event",
      entity_id: EVENT_ACME_1,
      diff: { capacity: { from: 250, to: 300 } },
      created_at: isoOffset(-12)
    }
  ];

  return {
    customers,
    adminUsers,
    events,
    operators,
    eventOperators,
    stages: [],
    stageOperators: [],
    attendees,
    scans,
    meetpointConfigs,
    syncRuns,
    audit,
    extraCredentials: [],
    snapshotRecipients: new Map()
  };
}

let state: MockState = seedState();

/** Resets the mock to its seed state. Used by tests for isolation. */
export function resetMockState(): void {
  idCounter = 1000;
  state = seedState();
}

// ---- Scope helpers ----------------------------------------------------------

interface Actor {
  id: string;
  role: "portal_admin" | "customer_admin";
  customerId: string | null;
}

function actorFromToken(token: string | null): Actor {
  if (!token) {
    throw new ApiError(401, "UNAUTHORIZED", "Missing access token");
  }
  const claims = decodeToken(token);
  if (!claims) {
    throw new ApiError(401, "UNAUTHORIZED", "Invalid access token");
  }
  return { id: claims.sub, role: claims.role, customerId: claims.customer_id };
}

function requirePortalAdmin(actor: Actor): void {
  if (actor.role !== "portal_admin") {
    throw new ApiError(403, "OUT_OF_SCOPE", "Portal admin access required");
  }
}

function requireCustomer(actor: Actor, customerId: string): void {
  if (actor.role === "portal_admin") {
    return;
  }
  if (actor.customerId !== customerId) {
    throw new ApiError(403, "OUT_OF_SCOPE", "Resource belongs to another customer");
  }
}

function requireEvent(actor: Actor, eventId: string): Event {
  const event = state.events.find((e) => e.id === eventId);
  if (!event) {
    throw new ApiError(404, "NOT_FOUND", "Event not found");
  }
  requireCustomer(actor, event.customer_id);
  return event;
}

// ---- Latency ----------------------------------------------------------------

function delay(ms = 120): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---- Derived helpers --------------------------------------------------------

function attendeeStatus(attendeeId: string): Attendee["status"] {
  const scans = state.scans
    .filter((s) => s.attendee_id === attendeeId)
    .sort((a, b) => b.scanned_at.localeCompare(a.scanned_at));
  const last = scans[0];
  if (!last) {
    return "NOT_SCANNED";
  }
  return last.direction === "ENTER" ? "IN_ROOM" : "OUT_OF_ROOM";
}

interface Page<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

function paginate<T>(rows: T[], page = 1, limit = 50): Page<T> {
  const safeLimit = Math.min(Math.max(limit, 1), 200);
  const safePage = Math.max(page, 1);
  const start = (safePage - 1) * safeLimit;
  return {
    data: rows.slice(start, start + safeLimit),
    total: rows.length,
    page: safePage,
    limit: safeLimit
  };
}

function randomHex(bytes: number): string {
  let out = "";
  for (let i = 0; i < bytes; i += 1) {
    out += Math.floor(Math.random() * 256)
      .toString(16)
      .padStart(2, "0");
  }
  return out;
}

function recordAudit(
  actor: Actor,
  action: string,
  entityType: string,
  entityId: string | null,
  customerId: string | null,
  diff: Record<string, unknown> | null = null
): void {
  state.audit.unshift({
    id: newId(),
    admin_user_id: actor.id,
    customer_id: customerId,
    action,
    entity_type: entityType,
    entity_id: entityId,
    diff,
    created_at: nowIso()
  });
}

// ---- The mock API ----------------------------------------------------------
// One method per OpenAPI operation. Each accepts the bearer token (or null
// for the public login route) plus typed inputs and returns typed shapes.

export interface MeetpointConfigPut {
  base_url?: string;
  username: string;
  password: string;
  meetpoint_event_id: string;
  language_id?: string;
  invitation_status_ids?: number[];
  polling_enabled?: boolean;
  polling_interval_seconds?: number;
}

export const mockApi = {
  // ---- Auth ----
  async login(email: string, password: string): Promise<{ access_token: string; token_type: "Bearer"; refresh_token: string }> {
    await delay();
    const all = [...CREDENTIALS, ...state.extraCredentials];
    const cred = all.find(
      (c) => c.email.toLowerCase() === email.trim().toLowerCase() && c.password === password
    );
    const user = cred
      ? state.adminUsers.find((u) => u.id === cred.userId && u.status === "ACTIVE")
      : undefined;
    if (!cred || !user) {
      throw new ApiError(401, "INVALID_CREDENTIALS", "Invalid email or password");
    }
    user.last_login_at = nowIso();
    const token = makeToken({
      sub: user.id,
      email: user.email,
      name: user.name,
      role: user.role,
      customer_id: user.customer_id,
      exp: Math.floor(Date.now() / 1000) + 24 * 3600
    });
    return { access_token: token, token_type: "Bearer", refresh_token: `mock-refresh-${user.id}` };
  },

  async logout(): Promise<void> {
    await delay(60);
  },

  async getMe(token: string | null): Promise<{ id: string; email: string; name: string; role: "portal_admin" | "customer_admin"; customer_id: string | null }> {
    await delay(60);
    const actor = actorFromToken(token);
    const user = state.adminUsers.find((u) => u.id === actor.id);
    if (!user) {
      throw new ApiError(401, "UNAUTHORIZED", "Unknown user");
    }
    return {
      id: user.id,
      email: user.email,
      name: user.name,
      role: user.role,
      customer_id: user.customer_id
    };
  },

  // ---- Customers ----
  async listCustomers(token: string | null, status?: Customer["status"]): Promise<Customer[]> {
    await delay();
    const actor = actorFromToken(token);
    requirePortalAdmin(actor);
    return state.customers.filter((c) => !status || c.status === status);
  },

  async createCustomer(token: string | null, body: { name: string; slug: string }): Promise<Customer> {
    await delay();
    const actor = actorFromToken(token);
    requirePortalAdmin(actor);
    if (!/^[a-z0-9-]+$/.test(body.slug)) {
      throw new ApiError(400, "INVALID_SLUG", "Slug must be lowercase letters, digits and hyphens");
    }
    if (state.customers.some((c) => c.slug === body.slug)) {
      throw new ApiError(409, "SLUG_TAKEN", "A customer with this slug already exists");
    }
    const customer: Customer = {
      id: newId(),
      name: body.name,
      slug: body.slug,
      status: "ACTIVE",
      created_at: nowIso()
    };
    state.customers.push(customer);
    recordAudit(actor, "customer.create", "customer", customer.id, customer.id);
    return customer;
  },

  async getCustomer(token: string | null, id: string): Promise<Customer> {
    await delay(60);
    const actor = actorFromToken(token);
    requirePortalAdmin(actor);
    const customer = state.customers.find((c) => c.id === id);
    if (!customer) {
      throw new ApiError(404, "NOT_FOUND", "Customer not found");
    }
    return customer;
  },

  async updateCustomer(token: string | null, id: string, body: { name?: string; status?: Customer["status"] }): Promise<Customer> {
    await delay();
    const actor = actorFromToken(token);
    requirePortalAdmin(actor);
    const customer = state.customers.find((c) => c.id === id);
    if (!customer) {
      throw new ApiError(404, "NOT_FOUND", "Customer not found");
    }
    const diff: Record<string, unknown> = {};
    if (body.name !== undefined && body.name !== customer.name) {
      diff.name = { from: customer.name, to: body.name };
      customer.name = body.name;
    }
    if (body.status !== undefined && body.status !== customer.status) {
      diff.status = { from: customer.status, to: body.status };
      customer.status = body.status;
    }
    recordAudit(actor, "customer.update", "customer", customer.id, customer.id, diff);
    return customer;
  },

  // ---- Admin users ----
  async listAdminUsers(token: string | null): Promise<AdminUser[]> {
    await delay();
    const actor = actorFromToken(token);
    if (actor.role === "portal_admin") {
      return [...state.adminUsers];
    }
    return state.adminUsers.filter(
      (u) => u.role === "customer_admin" && u.customer_id === actor.customerId
    );
  },

  async createAdminUser(
    token: string | null,
    body: { email: string; name: string; role: "portal_admin" | "customer_admin"; customer_id?: string | null; password: string }
  ): Promise<AdminUser> {
    await delay();
    const actor = actorFromToken(token);
    if (body.password.length < 12) {
      throw new ApiError(400, "PASSWORD_TOO_SHORT", "Password must be at least 12 characters");
    }
    if (actor.role === "customer_admin") {
      if (body.role !== "customer_admin") {
        throw new ApiError(403, "OUT_OF_SCOPE", "You can only create customer admins");
      }
      if (body.customer_id && body.customer_id !== actor.customerId) {
        throw new ApiError(403, "OUT_OF_SCOPE", "You can only create users for your own customer");
      }
    }
    if (body.role === "customer_admin" && !body.customer_id && actor.role === "portal_admin") {
      throw new ApiError(400, "CUSTOMER_REQUIRED", "customer_admin requires a customer_id");
    }
    if (state.adminUsers.some((u) => u.email.toLowerCase() === body.email.toLowerCase())) {
      throw new ApiError(409, "EMAIL_TAKEN", "An admin user with this email already exists");
    }
    const customerId =
      body.role === "portal_admin"
        ? null
        : (body.customer_id ?? actor.customerId);
    const user: AdminUser = {
      id: newId(),
      email: body.email,
      name: body.name,
      role: body.role,
      customer_id: customerId,
      status: "ACTIVE",
      last_login_at: null,
      created_at: nowIso()
    };
    state.adminUsers.push(user);
    state.extraCredentials.push({ email: body.email, password: body.password, userId: user.id });
    recordAudit(actor, "admin_user.create", "admin_user", user.id, customerId);
    return user;
  },

  async inviteAdminUser(
    token: string | null,
    body: { email: string; name: string; role: "portal_admin" | "customer_admin"; customer_id?: string | null }
  ): Promise<AdminUser & { email_sent: boolean; email_warning?: string }> {
    // Mirrors the admin-api invite endpoint. The mock fakes a successful
    // delivery; a real send happens only against the live API.
    await delay();
    const actor = actorFromToken(token);
    if (actor.role === "customer_admin") {
      if (body.role !== "customer_admin") {
        throw new ApiError(403, "OUT_OF_SCOPE", "You can only invite customer admins");
      }
      if (body.customer_id && body.customer_id !== actor.customerId) {
        throw new ApiError(403, "OUT_OF_SCOPE", "You can only invite users for your own customer");
      }
    }
    if (body.role === "customer_admin" && !body.customer_id && actor.role === "portal_admin") {
      throw new ApiError(400, "CUSTOMER_REQUIRED", "customer_admin requires a customer_id");
    }
    if (state.adminUsers.some((u) => u.email.toLowerCase() === body.email.toLowerCase())) {
      throw new ApiError(409, "EMAIL_TAKEN", "An admin user with this email already exists");
    }
    const customerId =
      body.role === "portal_admin"
        ? null
        : (body.customer_id ?? actor.customerId);
    const user: AdminUser = {
      id: newId(),
      email: body.email,
      name: body.name,
      role: body.role,
      customer_id: customerId,
      status: "ACTIVE",
      last_login_at: null,
      created_at: nowIso()
    };
    state.adminUsers.push(user);
    // The mock never knows the temp password — that's intentional. The real
    // API generates and discards it. We seed a known mock password so the
    // newcomer can sign in inside the mock playground.
    state.extraCredentials.push({ email: body.email, password: "MockTempPass12!", userId: user.id });
    recordAudit(actor, "admin_user.invite", "admin_user", user.id, customerId);
    return { ...user, email_sent: true };
  },

  async updateAdminUser(token: string | null, id: string, body: { name?: string; status?: AdminUser["status"] }): Promise<AdminUser> {
    await delay();
    const actor = actorFromToken(token);
    const user = state.adminUsers.find((u) => u.id === id);
    if (!user) {
      throw new ApiError(404, "NOT_FOUND", "Admin user not found");
    }
    if (actor.role === "customer_admin" && user.customer_id !== actor.customerId) {
      throw new ApiError(403, "OUT_OF_SCOPE", "Admin user belongs to another customer");
    }
    const diff: Record<string, unknown> = {};
    if (body.name !== undefined && body.name !== user.name) {
      diff.name = { from: user.name, to: body.name };
      user.name = body.name;
    }
    if (body.status !== undefined && body.status !== user.status) {
      diff.status = { from: user.status, to: body.status };
      user.status = body.status;
    }
    recordAudit(actor, "admin_user.update", "admin_user", user.id, user.customer_id, diff);
    return user;
  },

  async resetAdminUserPassword(token: string | null, id: string, newPassword: string): Promise<void> {
    await delay();
    const actor = actorFromToken(token);
    if (newPassword.length < 12) {
      throw new ApiError(400, "PASSWORD_TOO_SHORT", "Password must be at least 12 characters");
    }
    const user = state.adminUsers.find((u) => u.id === id);
    if (!user) {
      throw new ApiError(404, "NOT_FOUND", "Admin user not found");
    }
    if (actor.role === "customer_admin") {
      if (user.role === "portal_admin" || user.customer_id !== actor.customerId) {
        throw new ApiError(403, "OUT_OF_SCOPE", "You may not reset this user's password");
      }
    }
    const existing = state.extraCredentials.find((c) => c.userId === id);
    if (existing) {
      existing.password = newPassword;
    } else {
      state.extraCredentials.push({ email: user.email, password: newPassword, userId: id });
    }
    recordAudit(actor, "admin_user.reset_password", "admin_user", id, user.customer_id);
  },

  // ---- Events ----
  async listEvents(
    token: string | null,
    filters: { customer_id?: string; status?: Event["status"] } = {}
  ): Promise<Event[]> {
    await delay();
    const actor = actorFromToken(token);
    let rows = [...state.events];
    if (actor.role === "customer_admin") {
      rows = rows.filter((e) => e.customer_id === actor.customerId);
    } else if (filters.customer_id) {
      rows = rows.filter((e) => e.customer_id === filters.customer_id);
    }
    if (filters.status) {
      rows = rows.filter((e) => e.status === filters.status);
    }
    return rows.sort((a, b) => b.active_from.localeCompare(a.active_from));
  },

  async createEvent(
    token: string | null,
    body: { customer_id: string; name: string; active_from: string; active_to: string; capacity?: number | null; webhook_url?: string | null }
  ): Promise<Event> {
    await delay();
    const actor = actorFromToken(token);
    requireCustomer(actor, body.customer_id);
    const customer = state.customers.find((c) => c.id === body.customer_id);
    if (!customer) {
      throw new ApiError(404, "NOT_FOUND", "Customer not found");
    }
    if (customer.status === "DISABLED") {
      throw new ApiError(400, "CUSTOMER_DISABLED", "Cannot create an event for a disabled customer");
    }
    if (new Date(body.active_to) <= new Date(body.active_from)) {
      throw new ApiError(400, "INVALID_WINDOW", "active_to must be after active_from");
    }
    const event: Event = {
      id: newId(),
      customer_id: body.customer_id,
      name: body.name,
      active_from: body.active_from,
      active_to: body.active_to,
      status: "ACTIVE",
      capacity: body.capacity ?? null,
      webhook_url: body.webhook_url ?? null,
      created_at: nowIso(),
      went_live_at: null,
      meetpoint_polling_enabled: false
    };
    state.events.push(event);
    recordAudit(actor, "event.create", "event", event.id, event.customer_id);
    return event;
  },

  async getEvent(token: string | null, id: string): Promise<Event> {
    await delay(60);
    const actor = actorFromToken(token);
    return requireEvent(actor, id);
  },

  async updateEvent(
    token: string | null,
    id: string,
    body: { name?: string; active_from?: string; active_to?: string; status?: Event["status"]; capacity?: number | null; webhook_url?: string | null; went_live_at?: string | null }
  ): Promise<Event> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, id);
    const nextFrom = body.active_from ?? event.active_from;
    const nextTo = body.active_to ?? event.active_to;
    if (new Date(nextTo) <= new Date(nextFrom)) {
      throw new ApiError(400, "INVALID_WINDOW", "active_to must be after active_from");
    }
    const diff: Record<string, unknown> = {};
    for (const key of ["name", "active_from", "active_to", "status", "capacity", "webhook_url", "went_live_at"] as const) {
      const value = body[key];
      if (value !== undefined && value !== event[key]) {
        diff[key] = { from: event[key], to: value };
        // typed assignment per field
        if (key === "name" && typeof value === "string") {
          event.name = value;
        } else if (key === "active_from" && typeof value === "string") {
          event.active_from = value;
        } else if (key === "active_to" && typeof value === "string") {
          event.active_to = value;
        } else if (key === "status" && (value === "ACTIVE" || value === "CLOSED")) {
          event.status = value;
        } else if (key === "capacity") {
          event.capacity = typeof value === "number" ? value : null;
        } else if (key === "webhook_url") {
          event.webhook_url = typeof value === "string" ? value : null;
        } else if (key === "went_live_at") {
          // CR-11. null clears the marker ("back to test mode").
          event.went_live_at = typeof value === "string" ? value : null;
        }
      }
    }
    recordAudit(actor, "event.update", "event", event.id, event.customer_id, diff);
    return event;
  },

  // CR-11. Mock go-live: stamps went_live_at = now() (moving cutoff).
  async goLive(token: string | null, id: string): Promise<Event> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, id);
    const from = event.went_live_at;
    event.went_live_at = nowIso();
    recordAudit(actor, "event.go_live", "event", event.id, event.customer_id, {
      went_live_at: { from, to: event.went_live_at }
    });
    return event;
  },

  async rotateWebhookSecret(token: string | null, id: string): Promise<{ webhook_secret: string }> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, id);
    recordAudit(actor, "event.rotate_webhook_secret", "event", event.id, event.customer_id);
    return { webhook_secret: randomHex(32) };
  },

  async testWebhook(token: string | null, id: string): Promise<{ status_code: number }> {
    await delay(200);
    const actor = actorFromToken(token);
    const event = requireEvent(actor, id);
    if (!event.webhook_url) {
      throw new ApiError(400, "NO_WEBHOOK_URL", "The event has no webhook URL configured");
    }
    return { status_code: 200 };
  },

  // Live attendance counts — derived from the fixture scans the same way the
  // real admin-api derives them from the shared scans table. Zeros, never
  // null, for an event with no scans.
  async getEventLiveCounts(token: string | null, id: string): Promise<LiveCounts> {
    await delay(80);
    const actor = actorFromToken(token);
    requireEvent(actor, id);
    const rows = state.scans.filter((s) => s.event_id === id);

    // Last scan direction per attendee → currently_in.
    const lastByAttendee = new Map<string, Scan>();
    for (const scan of rows) {
      const prev = lastByAttendee.get(scan.attendee_id);
      if (!prev || scan.scanned_at > prev.scanned_at) {
        lastByAttendee.set(scan.attendee_id, scan);
      }
    }
    const enteredIds = new Set(
      rows.filter((s) => s.direction === "ENTER").map((s) => s.attendee_id)
    );
    const exitedIds = new Set(
      rows.filter((s) => s.direction === "EXIT").map((s) => s.attendee_id)
    );
    let currentlyIn = 0;
    for (const scan of lastByAttendee.values()) {
      if (scan.direction === "ENTER") {
        currentlyIn += 1;
      }
    }
    // Per-stage breakdown (CR-04 + admin CR-01 follow-up). One entry per stage
    // on this event with currently_in derived from each attendee's LAST scan
    // when that scan was an ENTER into the stage. Empty array when the event
    // has no stages.
    const eventStages = state.stages
      .filter((s) => s.event_id === id)
      .sort(
        (a, b) =>
          a.display_order - b.display_order || a.name.localeCompare(b.name)
      );
    const perStage = eventStages.map((stage) => {
      let inStage = 0;
      for (const last of lastByAttendee.values()) {
        // The admin OpenAPI's Scan schema doesn't yet model scan.stage_id
        // (scan-side change; admin pulls it from the shared DB via live-counts
        // rather than via the admin Scan list). Narrow with a local cast so
        // the mock can mirror the per-stage SQL the real backend runs.
        const lastWithStage = last as typeof last & { stage_id?: string | null };
        if (last.direction === "ENTER" && lastWithStage.stage_id === stage.id) {
          inStage += 1;
        }
      }
      return {
        stage_id: stage.id,
        stage_name: stage.name,
        currently_in: inStage,
        capacity: stage.capacity
      };
    });

    return {
      currently_in: currentlyIn,
      total_entered: enteredIds.size,
      total_exited: exitedIds.size,
      total_scanned: lastByAttendee.size,
      per_stage: perStage
    };
  },

  // Mock equivalent of GET /events/:id/monitor-link. The real backend signs
  // the URL with events.webhook_secret; the mock just produces a stable
  // 64-char hex string so the UI flow is exercisable end-to-end.
  async getEventMonitorLink(
    token: string | null,
    id: string
  ): Promise<{ url: string }> {
    await delay(80);
    const actor = actorFromToken(token);
    requireEvent(actor, id);
    const fakeToken = randomHex(32);
    return {
      url: `https://attendik-monitor.fly.dev/e/${id}?token=${fakeToken}`
    };
  },

  // Monitor CR-02 — snapshot recipient list management. In-memory only;
  // the real backend stores rows in `event_snapshot_recipients`.
  async listSnapshotRecipients(
    token: string | null,
    eventId: string
  ): Promise<{ data: { id: string; email: string; created_at: string; created_by: string | null }[] }> {
    await delay();
    const actor = actorFromToken(token);
    requireEvent(actor, eventId);
    const rows = state.snapshotRecipients.get(eventId) ?? [];
    return { data: [...rows] };
  },

  async setSnapshotRecipients(
    token: string | null,
    eventId: string,
    emails: string[]
  ): Promise<{ data: { id: string; email: string; created_at: string; created_by: string | null }[] }> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const seen = new Set<string>();
    const rows: { id: string; email: string; created_at: string; created_by: string | null }[] = [];
    for (const raw of emails) {
      const trimmed = raw.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      if (!emailRe.test(trimmed)) {
        throw new ApiError(400, "BAD_REQUEST", `Invalid recipient email: ${trimmed}`);
      }
      seen.add(key);
      rows.push({
        id: newId(),
        email: trimmed,
        created_at: nowIso(),
        created_by: actor.id
      });
    }
    state.snapshotRecipients.set(eventId, rows);
    recordAudit(
      actor,
      "event.snapshot_recipients_update",
      "event",
      eventId,
      event.customer_id,
      { recipients_count: rows.length }
    );
    return { data: [...rows] };
  },

  // ---- Operators ----
  async listOperators(token: string | null, customerId?: string): Promise<Operator[]> {
    await delay();
    const actor = actorFromToken(token);
    let rows = [...state.operators];
    if (actor.role === "customer_admin") {
      rows = rows.filter((o) => o.customer_id === actor.customerId);
    } else if (customerId) {
      rows = rows.filter((o) => o.customer_id === customerId);
    }
    return rows;
  },

  async createOperator(
    token: string | null,
    body: { customer_id: string; email: string; name: string; password: string }
  ): Promise<Operator> {
    await delay();
    const actor = actorFromToken(token);
    if (body.password.length < 12) {
      throw new ApiError(400, "PASSWORD_TOO_SHORT", "Password must be at least 12 characters");
    }
    requireCustomer(actor, body.customer_id);
    if (state.operators.some((o) => o.email.toLowerCase() === body.email.toLowerCase())) {
      throw new ApiError(409, "EMAIL_TAKEN", "An operator with this email already exists");
    }
    const operator: Operator = {
      id: newId(),
      customer_id: body.customer_id,
      email: body.email,
      name: body.name,
      status: "ACTIVE",
      created_at: nowIso()
    };
    state.operators.push(operator);
    recordAudit(actor, "operator.create", "operator", operator.id, operator.customer_id);
    return operator;
  },

  async getOperator(token: string | null, id: string): Promise<Operator> {
    await delay(60);
    const actor = actorFromToken(token);
    const operator = state.operators.find((o) => o.id === id);
    if (!operator) {
      throw new ApiError(404, "NOT_FOUND", "Operator not found");
    }
    requireCustomer(actor, operator.customer_id);
    return operator;
  },

  async updateOperator(token: string | null, id: string, body: { name?: string; status?: Operator["status"] }): Promise<Operator> {
    await delay();
    const actor = actorFromToken(token);
    const operator = state.operators.find((o) => o.id === id);
    if (!operator) {
      throw new ApiError(404, "NOT_FOUND", "Operator not found");
    }
    requireCustomer(actor, operator.customer_id);
    const diff: Record<string, unknown> = {};
    if (body.name !== undefined && body.name !== operator.name) {
      diff.name = { from: operator.name, to: body.name };
      operator.name = body.name;
    }
    if (body.status !== undefined && body.status !== operator.status) {
      diff.status = { from: operator.status, to: body.status };
      operator.status = body.status;
    }
    recordAudit(actor, "operator.update", "operator", operator.id, operator.customer_id, diff);
    return operator;
  },

  async resetOperatorPassword(token: string | null, id: string, newPassword: string): Promise<void> {
    await delay();
    const actor = actorFromToken(token);
    if (newPassword.length < 12) {
      throw new ApiError(400, "PASSWORD_TOO_SHORT", "Password must be at least 12 characters");
    }
    const operator = state.operators.find((o) => o.id === id);
    if (!operator) {
      throw new ApiError(404, "NOT_FOUND", "Operator not found");
    }
    requireCustomer(actor, operator.customer_id);
    recordAudit(actor, "operator.reset_password", "operator", operator.id, operator.customer_id);
  },

  // ---- Event operators ----
  async listEventOperators(
    token: string | null,
    eventId: string
  ): Promise<AssignedOperator[]> {
    await delay();
    const actor = actorFromToken(token);
    requireEvent(actor, eventId);
    const activeEventIds = new Set(
      state.events.filter((e) => e.status === "ACTIVE").map((e) => e.id)
    );
    const ids = state.eventOperators
      .filter((eo) => eo.event_id === eventId)
      .map((eo) => eo.operator_id);
    return state.operators
      .filter((o) => ids.includes(o.id))
      .map((o) => ({
        ...o,
        // Other ACTIVE events (excluding this one) the operator is assigned to.
        other_active_events_count: state.eventOperators.filter(
          (eo) =>
            eo.operator_id === o.id &&
            eo.event_id !== eventId &&
            activeEventIds.has(eo.event_id)
        ).length
      }));
  },

  async assignEventOperator(token: string | null, eventId: string, operatorId: string): Promise<{ event_id: string; operator_id: string; created_at: string }> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    const operator = state.operators.find((o) => o.id === operatorId);
    if (!operator) {
      throw new ApiError(404, "NOT_FOUND", "Operator not found");
    }
    if (operator.customer_id !== event.customer_id) {
      throw new ApiError(403, "CROSS_CUSTOMER", "Operator and event belong to different customers");
    }
    // Overlap check: half-open interval [active_from, active_to)
    const conflicts = state.eventOperators
      .filter((eo) => eo.operator_id === operatorId && eo.event_id !== eventId)
      .map((eo) => state.events.find((e) => e.id === eo.event_id))
      .filter((e): e is Event => Boolean(e))
      .filter(
        (e) =>
          new Date(e.active_from) < new Date(event.active_to) &&
          new Date(event.active_from) < new Date(e.active_to)
      );
    if (conflicts.length > 0) {
      const err = new ApiError(
        409,
        "OVERLAP",
        "Operator already assigned to overlapping event(s)"
      );
      err.conflicts = conflicts.map((e) => ({
        id: e.id,
        name: e.name,
        active_from: e.active_from,
        active_to: e.active_to
      }));
      throw err;
    }
    if (state.eventOperators.some((eo) => eo.event_id === eventId && eo.operator_id === operatorId)) {
      // already assigned — idempotent
      return { event_id: eventId, operator_id: operatorId, created_at: nowIso() };
    }
    state.eventOperators.push({ event_id: eventId, operator_id: operatorId });
    recordAudit(actor, "event_operator.assign", "event_operator", operatorId, event.customer_id);
    return { event_id: eventId, operator_id: operatorId, created_at: nowIso() };
  },

  async unassignEventOperator(token: string | null, eventId: string, operatorId: string): Promise<void> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    const idx = state.eventOperators.findIndex(
      (eo) => eo.event_id === eventId && eo.operator_id === operatorId
    );
    if (idx === -1) {
      throw new ApiError(404, "NOT_FOUND", "Assignment not found");
    }
    state.eventOperators.splice(idx, 1);
    recordAudit(actor, "event_operator.unassign", "event_operator", operatorId, event.customer_id);
  },

  // ---- Attendees ----
  async listAttendees(
    token: string | null,
    eventId: string,
    filters: {
      source?: Attendee["source"];
      status?: Attendee["status"];
      q?: string;
      sort?: "name" | "email" | "company" | "source" | "status";
      dir?: "asc" | "desc";
      page?: number;
      limit?: number;
    } = {}
  ): Promise<Page<Attendee>> {
    await delay();
    const actor = actorFromToken(token);
    requireEvent(actor, eventId);
    let rows = state.attendees
      .filter((a) => a.event_id === eventId)
      .map((a) => ({ ...a, status: attendeeStatus(a.id) }));
    if (filters.source) {
      rows = rows.filter((a) => a.source === filters.source);
    }
    if (filters.status) {
      rows = rows.filter((a) => a.status === filters.status);
    }
    // Free-text search: whitespace tokens AND-ed across name, email, company.
    const tokens = (filters.q ?? "")
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter((t) => t.length > 0);
    if (tokens.length > 0) {
      rows = rows.filter((a) => {
        const haystack = [a.name, a.email, a.company]
          .filter((v): v is string => Boolean(v))
          .join(" ")
          .toLowerCase();
        return tokens.every((t) => haystack.includes(t));
      });
    }
    // Sort (default: name ascending), with an id tiebreaker for stable paging —
    // mirrors the server's whitelisted ORDER BY (status sorts by its text value).
    const sortKey = filters.sort ?? "name";
    const dirMul = filters.dir === "desc" ? -1 : 1;
    rows = [...rows].sort((a, b) => {
      const av = (a[sortKey] ?? "").toLowerCase();
      const bv = (b[sortKey] ?? "").toLowerCase();
      if (av < bv) return -1 * dirMul;
      if (av > bv) return 1 * dirMul;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
    return paginate(rows, filters.page, filters.limit);
  },

  async createAttendee(
    token: string | null,
    eventId: string,
    body: {
      name: string;
      email?: string | null;
      company?: string | null;
      // CR-07
      group_names?: string[];
    }
  ): Promise<Attendee> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    const attendee: Attendee = {
      id: newId(),
      event_id: eventId,
      badge_id: newId(),
      name: body.name,
      email: body.email ?? null,
      company: body.company ?? null,
      group_names: normaliseMockGroupNames(body.group_names),
      source: "manual",
      status: "NOT_SCANNED",
      meetpoint_participant_id: null,
      meetpoint_contact_id: null,
      created_at: nowIso(),
      updated_at: nowIso()
    };
    state.attendees.push(attendee);
    recordAudit(actor, "attendee.create", "attendee", attendee.id, event.customer_id);
    return attendee;
  },

  async updateAttendee(
    token: string | null,
    eventId: string,
    attendeeId: string,
    body: {
      name?: string;
      email?: string | null;
      company?: string | null;
      // CR-07
      group_names?: string[];
    }
  ): Promise<Attendee> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    const attendee = state.attendees.find((a) => a.id === attendeeId && a.event_id === eventId);
    if (!attendee) {
      throw new ApiError(404, "NOT_FOUND", "Attendee not found");
    }
    const diff: Record<string, unknown> = {};
    if (body.name !== undefined && body.name !== attendee.name) {
      diff.name = { from: attendee.name, to: body.name };
      attendee.name = body.name;
    }
    if (body.email !== undefined && body.email !== attendee.email) {
      diff.email = { from: attendee.email, to: body.email };
      attendee.email = body.email ?? null;
    }
    if (body.company !== undefined && body.company !== attendee.company) {
      diff.company = { from: attendee.company, to: body.company };
      attendee.company = body.company ?? null;
    }
    if (body.group_names !== undefined) {
      const next = normaliseMockGroupNames(body.group_names);
      if (
        next.length !== attendee.group_names.length ||
        next.some((g, i) => g !== attendee.group_names[i])
      ) {
        diff.group_names = { from: attendee.group_names, to: next };
        attendee.group_names = next;
      }
    }
    attendee.updated_at = nowIso();
    recordAudit(actor, "attendee.update", "attendee", attendee.id, event.customer_id, diff);
    return { ...attendee, status: attendeeStatus(attendee.id) };
  },

  async deleteAttendee(token: string | null, eventId: string, attendeeId: string): Promise<void> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    const idx = state.attendees.findIndex((a) => a.id === attendeeId && a.event_id === eventId);
    if (idx === -1) {
      throw new ApiError(404, "NOT_FOUND", "Attendee not found");
    }
    if (state.scans.some((s) => s.attendee_id === attendeeId)) {
      throw new ApiError(409, "HAS_SCANS", "Attendee has scan history and cannot be deleted");
    }
    state.attendees.splice(idx, 1);
    recordAudit(actor, "attendee.delete", "attendee", attendeeId, event.customer_id);
  },

  // ---- Scans ----
  async emailScanLog(
    token: string | null,
    eventId: string,
    recipients: string[]
  ): Promise<{ recipients_count: number; message_ids: string[]; warning?: string }> {
    await delay(300);
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    if (!Array.isArray(recipients) || recipients.length === 0) {
      throw new ApiError(400, "BAD_REQUEST", "At least one recipient is required");
    }
    if (recipients.length > 10) {
      throw new ApiError(400, "BAD_REQUEST", "Maximum 10 recipients allowed");
    }
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const r of recipients) {
      if (!re.test(r)) {
        throw new ApiError(400, "BAD_REQUEST", `Invalid recipient: ${r}`);
      }
    }
    recordAudit(actor, "event.scan_log_email", "event", eventId, event.customer_id, {
      recipients_count: recipients.length
    });
    // Fabricate one message id per recipient — the real API uses Resend ids.
    return {
      recipients_count: recipients.length,
      message_ids: recipients.map((_, i) => `mock-msg-${String(i)}`)
    };
  },

  async listScans(
    token: string | null,
    eventId: string,
    filters: { operator_id?: string; direction?: Scan["direction"]; result_code?: Scan["result_code"]; page?: number; limit?: number; include_test?: boolean } = {}
  ): Promise<Page<Scan>> {
    await delay();
    const actor = actorFromToken(token);
    requireEvent(actor, eventId);
    let rows = state.scans
      .filter((s) => s.event_id === eventId)
      .sort((a, b) => b.scanned_at.localeCompare(a.scanned_at));
    if (filters.operator_id) {
      rows = rows.filter((s) => s.scanned_by === filters.operator_id);
    }
    if (filters.direction) {
      rows = rows.filter((s) => s.direction === filters.direction);
    }
    if (filters.result_code !== undefined) {
      rows = rows.filter((s) => s.result_code === filters.result_code);
    }
    return paginate(rows, filters.page, filters.limit);
  },

  // ---- Meetpoint config ----
  async getMeetpointConfig(token: string | null, eventId: string): Promise<MeetpointConfig> {
    await delay();
    const actor = actorFromToken(token);
    requireEvent(actor, eventId);
    const cfg = state.meetpointConfigs.get(eventId);
    if (!cfg) {
      throw new ApiError(404, "NOT_CONFIGURED", "No Meetpoint configuration for this event");
    }
    return { ...cfg, password: "••••••••" };
  },

  async putMeetpointConfig(token: string | null, eventId: string, body: MeetpointConfigPut): Promise<MeetpointConfig> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    const interval = body.polling_interval_seconds ?? 30;
    if (interval < 10 || interval > 300) {
      throw new ApiError(400, "INVALID_INTERVAL", "polling_interval_seconds must be between 10 and 300");
    }
    const existing = state.meetpointConfigs.get(eventId);
    const cfg: MeetpointConfig = {
      event_id: eventId,
      base_url: body.base_url ?? existing?.base_url ?? "https://platform.meetpoint.si",
      username: body.username,
      password: "••••••••",
      meetpoint_event_id: body.meetpoint_event_id,
      language_id: body.language_id ?? existing?.language_id ?? "EN",
      invitation_status_ids: body.invitation_status_ids ?? existing?.invitation_status_ids ?? [-1],
      polling_enabled: body.polling_enabled ?? existing?.polling_enabled ?? false,
      polling_interval_seconds: interval,
      last_synced_at: existing?.last_synced_at ?? null,
      last_sync_status: existing?.last_sync_status ?? null,
      last_sync_error: existing?.last_sync_error ?? null,
      created_at: existing?.created_at ?? nowIso(),
      updated_at: nowIso()
    };
    state.meetpointConfigs.set(eventId, cfg);
    recordAudit(actor, existing ? "meetpoint.update" : "meetpoint.create", "meetpoint_config", eventId, event.customer_id);
    return cfg;
  },

  async deleteMeetpointConfig(token: string | null, eventId: string): Promise<void> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    if (!state.meetpointConfigs.has(eventId)) {
      throw new ApiError(404, "NOT_CONFIGURED", "No Meetpoint configuration for this event");
    }
    state.meetpointConfigs.delete(eventId);
    recordAudit(actor, "meetpoint.delete", "meetpoint_config", eventId, event.customer_id);
  },

  async testMeetpointConnection(token: string | null, eventId: string): Promise<{ connected: true; participant_count: number }> {
    await delay(400);
    const actor = actorFromToken(token);
    requireEvent(actor, eventId);
    const cfg = state.meetpointConfigs.get(eventId);
    if (!cfg) {
      throw new ApiError(404, "NOT_CONFIGURED", "No Meetpoint configuration for this event");
    }
    // The mock MP server fixture returns 5 participants. Bad usernames fail.
    if (cfg.username.toLowerCase().includes("bad")) {
      throw new ApiError(502, "WRONG_CREDENTIALS", "Meetpoint rejected the credentials");
    }
    return { connected: true, participant_count: 5 };
  },

  // CR-02 — stub for the connect endpoint. Returns 3 deterministic events so
  // the picker UI is exercisable in mock mode. Wrong creds → 502.
  async connectMeetpoint(
    token: string | null,
    eventId: string,
    body: { username: string; password: string }
  ): Promise<{
    events: {
      event_id: string;
      name: string;
      start_at: string | null;
      end_at: string | null;
    }[];
  }> {
    await delay(400);
    const actor = actorFromToken(token);
    requireEvent(actor, eventId);
    if (!body.username || !body.password) {
      throw new ApiError(400, "VALIDATION_ERROR", "username and password are required");
    }
    if (body.username.toLowerCase().includes("bad")) {
      throw new ApiError(502, "WRONG_CREDENTIALS", "Meetpoint rejected the credentials");
    }
    return {
      events: [
        { event_id: "2519", name: "Tech Summit Ljubljana 2026", start_at: "2026-05-20T08:00:00Z", end_at: "2026-05-22T18:00:00Z" },
        { event_id: "2520", name: "Annual Industry Awards 2026", start_at: "2026-06-15T17:00:00Z", end_at: "2026-06-15T23:00:00Z" },
        { event_id: "2521", name: "Developer Bootcamp", start_at: "2026-07-01T09:00:00Z", end_at: "2026-07-03T17:00:00Z" }
      ]
    };
  },

  // ---- Meetpoint sync ----
  async listSyncRuns(token: string | null, eventId: string, page = 1, limit = 50): Promise<Page<SyncRun>> {
    await delay();
    const actor = actorFromToken(token);
    requireEvent(actor, eventId);
    const rows = state.syncRuns
      .filter((s) => s.event_id === eventId)
      .sort((a, b) => b.started_at.localeCompare(a.started_at));
    return paginate(rows, page, limit);
  },

  async triggerSync(
    token: string | null,
    eventId: string,
    cleanMissingAttendees = false
  ): Promise<{ sync_log_id: string; deleted_count: number }> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    const cfg = state.meetpointConfigs.get(eventId);
    if (!cfg) {
      throw new ApiError(404, "NOT_CONFIGURED", "No Meetpoint configuration for this event");
    }
    const running = state.syncRuns.find((s) => s.event_id === eventId && s.status === "RUNNING");
    if (running) {
      const err = new ApiError(409, "SYNC_IN_PROGRESS", "A sync is already running for this event");
      err.syncLogId = running.id;
      throw err;
    }
    // Authoritative clean: delete Meetpoint-sourced attendees with no scans
    // (the mock stand-in for "missing from the latest Meetpoint pull").
    let deletedCount = 0;
    if (cleanMissingAttendees) {
      const scanned = new Set(
        state.scans.filter((s) => s.event_id === eventId).map((s) => s.attendee_id)
      );
      const before = state.attendees.length;
      state.attendees = state.attendees.filter(
        (a) =>
          !(a.event_id === eventId && a.source === "meetpoint" && !scanned.has(a.id))
      );
      deletedCount = before - state.attendees.length;
    }
    const run: SyncRun = {
      id: newId(),
      event_id: eventId,
      mode: "MANUAL",
      triggered_by: actor.id,
      started_at: nowIso(),
      finished_at: null,
      status: "RUNNING",
      inserted_count: 0,
      updated_count: 0,
      skipped_count: 0,
      stage_inserted_count: 0,
      stage_updated_count: 0,
      stage_skipped_count: 0,
      error: null
    };
    state.syncRuns.unshift(run);
    recordAudit(actor, "meetpoint.sync", "meetpoint_sync", run.id, event.customer_id);
    // Resolve the run after a short delay to simulate the async sync.
    setTimeout(() => {
      run.status = "SUCCESS";
      run.finished_at = nowIso();
      run.inserted_count = 0;
      run.skipped_count = state.attendees.filter(
        (a) => a.event_id === eventId && a.source === "meetpoint"
      ).length;
      cfg.last_synced_at = nowIso();
      cfg.last_sync_status = "SUCCESS";
      cfg.last_sync_error = null;
    }, 1500);
    return { sync_log_id: run.id, deleted_count: deletedCount };
  },

  async getSyncRun(token: string | null, id: string): Promise<SyncRun> {
    await delay(60);
    const actor = actorFromToken(token);
    const run = state.syncRuns.find((s) => s.id === id);
    if (!run) {
      throw new ApiError(404, "NOT_FOUND", "Sync run not found");
    }
    requireEvent(actor, run.event_id);
    return run;
  },

  // ---- Stages (admin CR-01) ----
  async listStages(token: string | null, eventId: string): Promise<Stage[]> {
    await delay();
    const actor = actorFromToken(token);
    requireEvent(actor, eventId);
    return state.stages
      .filter((s) => s.event_id === eventId)
      .sort(
        (a, b) =>
          a.display_order - b.display_order ||
          a.created_at.localeCompare(b.created_at)
      );
  },

  async createStage(
    token: string | null,
    eventId: string,
    body: CreateStageRequest
  ): Promise<Stage> {
    await delay();
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    if (
      body.active_from &&
      body.active_to &&
      new Date(body.active_to) <= new Date(body.active_from)
    ) {
      throw new ApiError(400, "BAD_REQUEST", "active_to must be after active_from");
    }
    if (body.capacity !== undefined && body.capacity !== null && body.capacity <= 0) {
      throw new ApiError(400, "BAD_REQUEST", "capacity must be > 0");
    }
    const stage: Stage = {
      id: newId(),
      event_id: eventId,
      name: body.name,
      capacity: body.capacity ?? null,
      active_from: body.active_from ?? null,
      active_to: body.active_to ?? null,
      display_order: body.display_order ?? 0,
      created_at: nowIso(),
      updated_at: nowIso(),
      source: "manual",
      meetpoint_program_location_id: null,
      meetpoint_program_id: null,
      enforce_program_eligibility: body.enforce_program_eligibility ?? false
    };
    state.stages.push(stage);
    recordAudit(actor, "stage.create", "stage", stage.id, event.customer_id);
    return stage;
  },

  async getStage(token: string | null, id: string): Promise<Stage> {
    await delay(60);
    const actor = actorFromToken(token);
    const stage = state.stages.find((s) => s.id === id);
    if (!stage) {
      throw new ApiError(404, "NOT_FOUND", "Stage not found");
    }
    requireEvent(actor, stage.event_id);
    return stage;
  },

  async updateStage(
    token: string | null,
    id: string,
    body: UpdateStageRequest
  ): Promise<Stage> {
    await delay();
    const actor = actorFromToken(token);
    const stage = state.stages.find((s) => s.id === id);
    if (!stage) {
      throw new ApiError(404, "NOT_FOUND", "Stage not found");
    }
    const event = requireEvent(actor, stage.event_id);
    const diff: Record<string, unknown> = {};
    if (body.name !== undefined && body.name !== stage.name) {
      diff.name = { from: stage.name, to: body.name };
      stage.name = body.name;
    }
    if (body.capacity !== undefined && body.capacity !== stage.capacity) {
      diff.capacity = { from: stage.capacity, to: body.capacity };
      stage.capacity = body.capacity;
    }
    if (
      body.active_from !== undefined &&
      body.active_from !== stage.active_from
    ) {
      diff.active_from = { from: stage.active_from, to: body.active_from };
      stage.active_from = body.active_from;
    }
    if (body.active_to !== undefined && body.active_to !== stage.active_to) {
      diff.active_to = { from: stage.active_to, to: body.active_to };
      stage.active_to = body.active_to;
    }
    if (
      body.display_order !== undefined &&
      body.display_order !== stage.display_order
    ) {
      diff.display_order = { from: stage.display_order, to: body.display_order };
      stage.display_order = body.display_order;
    }
    if (
      body.enforce_program_eligibility !== undefined &&
      body.enforce_program_eligibility !== stage.enforce_program_eligibility
    ) {
      diff.enforce_program_eligibility = {
        from: stage.enforce_program_eligibility,
        to: body.enforce_program_eligibility
      };
      stage.enforce_program_eligibility = body.enforce_program_eligibility;
    }
    stage.updated_at = nowIso();
    recordAudit(actor, "stage.update", "stage", stage.id, event.customer_id, diff);
    return stage;
  },

  async deleteStage(token: string | null, id: string): Promise<void> {
    await delay();
    const actor = actorFromToken(token);
    const idx = state.stages.findIndex((s) => s.id === id);
    if (idx === -1) {
      throw new ApiError(404, "NOT_FOUND", "Stage not found");
    }
    const stage = state.stages[idx]!;
    const event = requireEvent(actor, stage.event_id);
    if (state.scans.some((s) => (s as Scan & { stage_id?: string | null }).stage_id === id)) {
      throw new ApiError(
        409,
        "STAGE_HAS_SCANS",
        "Stage has scan history and cannot be deleted"
      );
    }
    state.stages.splice(idx, 1);
    state.stageOperators = state.stageOperators.filter((so) => so.stage_id !== id);
    recordAudit(actor, "stage.delete", "stage", id, event.customer_id);
  },

  async getStageMonitorLink(token: string | null, id: string): Promise<{ url: string }> {
    await delay(80);
    const actor = actorFromToken(token);
    const stage = state.stages.find((s) => s.id === id);
    if (!stage) {
      throw new ApiError(404, "NOT_FOUND", "Stage not found");
    }
    requireEvent(actor, stage.event_id);
    const fakeToken = randomHex(32);
    return {
      url: `https://attendik-monitor.fly.dev/s/${id}?token=${fakeToken}`
    };
  },

  async listStageOperators(
    token: string | null,
    stageId: string
  ): Promise<Operator[]> {
    await delay();
    const actor = actorFromToken(token);
    const stage = state.stages.find((s) => s.id === stageId);
    if (!stage) {
      throw new ApiError(404, "NOT_FOUND", "Stage not found");
    }
    requireEvent(actor, stage.event_id);
    const ids = state.stageOperators
      .filter((so) => so.stage_id === stageId)
      .map((so) => so.operator_id);
    return state.operators.filter((o) => ids.includes(o.id));
  },

  async assignStageOperator(
    token: string | null,
    stageId: string,
    operatorId: string
  ): Promise<StageOperator> {
    await delay();
    const actor = actorFromToken(token);
    const stage = state.stages.find((s) => s.id === stageId);
    if (!stage) {
      throw new ApiError(404, "NOT_FOUND", "Stage not found");
    }
    const event = requireEvent(actor, stage.event_id);
    const operator = state.operators.find((o) => o.id === operatorId);
    if (!operator) {
      throw new ApiError(404, "NOT_FOUND", "Operator not found");
    }
    if (operator.customer_id !== event.customer_id) {
      throw new ApiError(
        403,
        "CROSS_CUSTOMER",
        "Operator and event belong to different customers"
      );
    }
    const onEvent = state.eventOperators.some(
      (eo) => eo.event_id === stage.event_id && eo.operator_id === operatorId
    );
    if (!onEvent) {
      throw new ApiError(
        409,
        "OPERATOR_NOT_ON_EVENT",
        "Operator must be assigned to the event before being assigned to a stage"
      );
    }
    // Overlap check: half-open intervals across OTHER events (the current
    // event is excluded so two stages of the same event can share an
    // operator).
    const conflicts = state.eventOperators
      .filter(
        (eo) => eo.operator_id === operatorId && eo.event_id !== stage.event_id
      )
      .map((eo) => state.events.find((e) => e.id === eo.event_id))
      .filter((e): e is Event => Boolean(e))
      .filter(
        (e) =>
          new Date(e.active_from) < new Date(event.active_to) &&
          new Date(event.active_from) < new Date(e.active_to)
      );
    if (conflicts.length > 0) {
      const err = new ApiError(
        409,
        "OVERLAP",
        "Operator already assigned to overlapping event(s)"
      );
      err.conflicts = conflicts.map((e) => ({
        id: e.id,
        name: e.name,
        active_from: e.active_from,
        active_to: e.active_to
      }));
      throw err;
    }
    if (
      state.stageOperators.some(
        (so) => so.stage_id === stageId && so.operator_id === operatorId
      )
    ) {
      // Idempotent — already assigned.
      const existing = state.stageOperators.find(
        (so) => so.stage_id === stageId && so.operator_id === operatorId
      )!;
      return { stage_id: stageId, operator_id: operatorId, created_at: existing.created_at };
    }
    const row = { stage_id: stageId, operator_id: operatorId, created_at: nowIso() };
    state.stageOperators.push(row);
    recordAudit(actor, "stage_operator.assign", "stage_operator", stageId, event.customer_id);
    return row;
  },

  async unassignStageOperator(
    token: string | null,
    stageId: string,
    operatorId: string
  ): Promise<void> {
    await delay();
    const actor = actorFromToken(token);
    const stage = state.stages.find((s) => s.id === stageId);
    if (!stage) {
      throw new ApiError(404, "NOT_FOUND", "Stage not found");
    }
    const event = requireEvent(actor, stage.event_id);
    const idx = state.stageOperators.findIndex(
      (so) => so.stage_id === stageId && so.operator_id === operatorId
    );
    if (idx === -1) {
      throw new ApiError(404, "NOT_FOUND", "Assignment not found");
    }
    state.stageOperators.splice(idx, 1);
    recordAudit(actor, "stage_operator.unassign", "stage_operator", stageId, event.customer_id);
  },

  // ---- CR-06: convert stage source ----
  async convertStageSource(
    token: string | null,
    stageId: string,
    source: "manual"
  ): Promise<Stage> {
    await delay();
    const actor = actorFromToken(token);
    const stage = state.stages.find((s) => s.id === stageId);
    if (!stage) {
      throw new ApiError(404, "NOT_FOUND", "Stage not found");
    }
    const event = requireEvent(actor, stage.event_id);
    if (source !== "manual") {
      throw new ApiError(
        400,
        "INVALID_SOURCE_TRANSITION",
        "Only meetpoint → manual is allowed"
      );
    }
    if (stage.source === "manual") {
      // Idempotent no-op.
      return stage;
    }
    const before = stage.source;
    stage.source = "manual";
    stage.updated_at = nowIso();
    recordAudit(actor, "stage.convert_source", "stage", stage.id, event.customer_id, {
      source: { from: before, to: "manual" }
    });
    return stage;
  },

  // ---- CR-06: Meetpoint event-summary / apply-summary ----
  async getMeetpointEventSummary(
    token: string | null,
    eventId: string
  ): Promise<MeetpointEventSummary> {
    await delay(120);
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    const cfg = state.meetpointConfigs.get(eventId);
    if (!cfg) {
      throw new ApiError(
        404,
        "NOT_FOUND",
        "No Meetpoint configuration for this event"
      );
    }
    // Mock pre-fill: just return the configured MP event id as a suggestion
    // and bracket the event's current window by an hour either side. Real
    // admin-api derives this from MP's program rows.
    return {
      event_name: `MP Event ${cfg.meetpoint_event_id}`,
      active_from: event.active_from,
      active_to: event.active_to,
      capacity: event.capacity ?? null
    };
  },

  async applyMeetpointSummary(
    token: string | null,
    eventId: string
  ): Promise<Event> {
    await delay(150);
    const actor = actorFromToken(token);
    const event = requireEvent(actor, eventId);
    const summary = await mockApi.getMeetpointEventSummary(token, eventId);
    if (!summary.active_from || !summary.active_to) {
      throw new ApiError(
        400,
        "MEETPOINT_NO_WINDOW",
        "Meetpoint did not return a usable active window"
      );
    }
    const before = { name: event.name, active_from: event.active_from, active_to: event.active_to };
    event.name = summary.event_name;
    event.active_from = summary.active_from;
    event.active_to = summary.active_to;
    recordAudit(
      actor,
      "event.apply_meetpoint_summary",
      "event",
      event.id,
      event.customer_id,
      {
        name: { from: before.name, to: event.name },
        active_from: { from: before.active_from, to: event.active_from },
        active_to: { from: before.active_to, to: event.active_to }
      }
    );
    return event;
  },

  // ---- Audit ----
  async listAudit(
    token: string | null,
    filters: { admin_user_id?: string; entity_type?: string; customer_id?: string; page?: number; limit?: number } = {}
  ): Promise<Page<AuditEntry>> {
    await delay();
    const actor = actorFromToken(token);
    let rows = [...state.audit];
    if (actor.role === "customer_admin") {
      rows = rows.filter((a) => a.customer_id === actor.customerId);
    } else if (filters.customer_id) {
      rows = rows.filter((a) => a.customer_id === filters.customer_id);
    }
    if (filters.admin_user_id) {
      rows = rows.filter((a) => a.admin_user_id === filters.admin_user_id);
    }
    if (filters.entity_type) {
      rows = rows.filter((a) => a.entity_type === filters.entity_type);
    }
    rows.sort((a, b) => b.created_at.localeCompare(a.created_at));
    return paginate(rows, filters.page, filters.limit);
  },

  // ---- Dashboard (aggregate) ----
  // Mirrors the admin-api /dashboard route over the in-memory state, scoped to
  // the actor. Attention is derived the same way the backend does so the UI is
  // exercisable end-to-end in mock mode.
  async getDashboard(token: string | null): Promise<DashboardSummary> {
    await delay();
    const actor = actorFromToken(token);
    const scoped = actor.role === "customer_admin";
    const now = Date.now();

    let events = [...state.events];
    if (scoped) {
      events = events.filter((e) => e.customer_id === actor.customerId);
    }

    const isActive = (e: Event): boolean =>
      e.status === "ACTIVE" &&
      new Date(e.active_from).getTime() <= now &&
      new Date(e.active_to).getTime() >= now;
    const isUpcoming = (e: Event): boolean =>
      e.status === "ACTIVE" && new Date(e.active_from).getTime() > now;

    const activeAll = events.filter(isActive);
    const upcomingAll = events
      .filter(isUpcoming)
      .sort((a, b) => a.active_from.localeCompare(b.active_from));
    const activeForCounts = activeAll.slice(0, 50);

    // Live counts per active event — reuse the existing mock derivation.
    let inRoomNow = 0;
    const activeEvents: DashboardSummary["active_events"] = [];
    for (const e of activeForCounts) {
      const lc = await mockApi.getEventLiveCounts(token, e.id);
      inRoomNow += lc.currently_in;
      activeEvents.push({
        id: e.id,
        name: e.name,
        active_from: e.active_from,
        active_to: e.active_to,
        capacity: e.capacity,
        currently_in: lc.currently_in,
        total_entered: lc.total_entered,
        total_exited: lc.total_exited,
        total_scanned: lc.total_scanned,
        per_stage: lc.per_stage
      });
    }

    // Polling comes from the MP config (the event flag in the mock fixture is
    // independent — the real backend joins the config row).
    const pollingFor = (eventId: string): boolean =>
      state.meetpointConfigs.get(eventId)?.polling_enabled === true;

    const attention: DashboardSummary["attention"] = [];
    const attentionEvents = events.filter(
      (e) => isActive(e) || pollingFor(e.id)
    );
    for (const e of attentionEvents) {
      const runs = state.syncRuns
        .filter((s) => s.event_id === e.id)
        .sort((a, b) => b.started_at.localeCompare(a.started_at));
      const latest = runs[0];
      if (!latest) continue;
      const polling = pollingFor(e.id);
      const failing = latest.status === "FAILED" || latest.status === "PARTIAL";
      if (polling && failing) {
        attention.push({
          severity: "destructive",
          kind: "POLLING_FAILING",
          label: e.name,
          detail:
            latest.error?.trim() ||
            "Polling is on but the last Meetpoint sync did not succeed.",
          event_id: e.id,
          tab: "integration"
        });
      } else if (latest.status === "FAILED") {
        attention.push({
          severity: "destructive",
          kind: "SYNC_FAILED",
          label: e.name,
          detail: latest.error?.trim() || "The last Meetpoint sync failed.",
          event_id: e.id,
          tab: "integration"
        });
      } else if (latest.status === "PARTIAL") {
        attention.push({
          severity: "warning",
          kind: "SYNC_PARTIAL",
          label: e.name,
          detail:
            latest.error?.trim() || "The last Meetpoint sync partially failed.",
          event_id: e.id,
          tab: "integration"
        });
      } else if (
        latest.status === "RUNNING" &&
        latest.finished_at === null &&
        now - new Date(latest.started_at).getTime() > 15 * 60 * 1000
      ) {
        attention.push({
          severity: "warning",
          kind: "SYNC_STUCK",
          label: e.name,
          detail: "A Meetpoint sync has been running for over 15 minutes.",
          event_id: e.id,
          tab: "integration"
        });
      }
    }
    for (const ae of activeEvents) {
      let stageOver = false;
      for (const stage of ae.per_stage) {
        if (stage.capacity == null || stage.capacity <= 0) continue;
        const ratio = stage.currently_in / stage.capacity;
        if (ratio >= 1) {
          stageOver = true;
          attention.push({
            severity: "destructive",
            kind: "STAGE_OVER_CAPACITY",
            label: `${ae.name} — ${stage.stage_name}`,
            detail: `${String(stage.currently_in)} / ${String(stage.capacity)} in stage`,
            event_id: ae.id,
            tab: null
          });
        } else if (ratio >= 0.8) {
          attention.push({
            severity: "warning",
            kind: "STAGE_NEAR_CAPACITY",
            label: `${ae.name} — ${stage.stage_name}`,
            detail: `${String(stage.currently_in)} / ${String(stage.capacity)} in stage`,
            event_id: ae.id,
            tab: null
          });
        }
      }
      if (!stageOver && ae.capacity != null && ae.capacity > 0 && ae.currently_in >= ae.capacity) {
        attention.push({
          severity: "destructive",
          kind: "EVENT_OVER_CAPACITY",
          label: ae.name,
          detail: `${String(ae.currently_in)} / ${String(ae.capacity)} in room`,
          event_id: ae.id,
          tab: null
        });
      }
    }
    attention.sort(
      (a, b) =>
        (a.severity === "destructive" ? 0 : 1) -
        (b.severity === "destructive" ? 0 : 1)
    );

    const eventName = (id: string): string =>
      state.events.find((e) => e.id === id)?.name ?? "";
    const recentSyncRuns: DashboardSummary["recent_sync_runs"] = [
      ...state.syncRuns
    ]
      .filter((r) => events.some((e) => e.id === r.event_id))
      .sort((a, b) => b.started_at.localeCompare(a.started_at))
      .slice(0, 10)
      .map((r) => ({
        id: r.id,
        event_id: r.event_id,
        event_name: eventName(r.event_id),
        mode: r.mode,
        status: r.status,
        started_at: r.started_at,
        finished_at: r.finished_at
      }));

    let auditRows = [...state.audit];
    if (scoped) {
      auditRows = auditRows.filter((a) => a.customer_id === actor.customerId);
    }
    const recentAudit = auditRows
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .slice(0, 10);

    let operators = [...state.operators];
    if (scoped) {
      operators = operators.filter((o) => o.customer_id === actor.customerId);
    }

    return {
      counts: {
        active_events: activeAll.length,
        upcoming_events: upcomingAll.length,
        total_events: events.length,
        in_room_now: inRoomNow,
        operators: operators.length,
        customers: scoped ? null : state.customers.length
      },
      active_events: activeEvents,
      upcoming_events: upcomingAll.map((e) => ({
        id: e.id,
        name: e.name,
        active_from: e.active_from,
        active_to: e.active_to
      })),
      attention: attention.slice(0, 8),
      recent_sync_runs: recentSyncRuns,
      recent_audit: recentAudit
    };
  }
};

export type MockApi = typeof mockApi;
